import React from 'react';
import { Button, ButtonProps, ListItem } from '@/components/ui';
import { IconDownload, IconLoader } from '@tabler/icons-react';
import { useDownloadFile } from '@/hooks/useDownloadFile';

export interface DownloadButtonProps extends Omit<ButtonProps, 'onClick'> {
  fileUrl?: string;
  apiEndpoint?: string;
  filename?: string;
  onDownload?: () => void;
  showToast?: boolean;
  successMessage?: string;
  errorMessage?: string;
  children?: React.ReactNode;
  asListItem?: boolean; // New prop for dropdown menu usage
}

/**
 * Download button component with loading state and progress
 */
export const DownloadButton: React.FC<DownloadButtonProps> = ({
  fileUrl,
  apiEndpoint,
  filename,
  onDownload,
  showToast = true,
  successMessage = 'File downloaded successfully',
  errorMessage = 'Failed to download file',
  children,
  disabled,
  asListItem = false,
  ...buttonProps
}) => {
  const { className, style } = buttonProps;
  const { download, downloadFromApi, isLoading } = useDownloadFile({
    showToast,
    successMessage,
    errorMessage,
  });

  const handleDownload = async () => {
    if (isLoading) return;

    try {
      let result;
      if (apiEndpoint) {
        // Use API endpoint for download
        result = await downloadFromApi(apiEndpoint, filename);
      } else if (fileUrl) {
        // Use direct file URL for download
        result = await download(fileUrl, filename);
      } else {
        return;
      }

      if (result.success) {
        onDownload?.();
      }
    } catch {
      // Best-effort: errors are surfaced via toast in useDownloadFile when enabled.
    }
  };

  // If used as ListItem in dropdown menu
  if (asListItem) {
    return (
      <ListItem
        startIcon={
          isLoading ? <IconLoader className="animate-spin" /> : <IconDownload />
        }
        primary={children || 'Download'}
        onClick={handleDownload}
        disabled={disabled || isLoading}
        className={className}
        style={style}
      />
    );
  }

  // Regular button usage
  return (
    <Button
      {...buttonProps}
      disabled={disabled || isLoading}
      onClick={handleDownload}
      startIcon={
        isLoading ? <IconLoader className="animate-spin" /> : <IconDownload />
      }
    >
      {children}
    </Button>
  );
};
